import React, { useEffect, useState } from 'react';
import { Tabs, Tab } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { getApiaryDetails, disableApiary, enableApiary, deleteApiary } from '../../../../../services/apiaryService';

import ApiaryProfile from './Components/ApiaryProfile';
import NectarObservations from './Components/NectarObservations';
import NectarExpenses from './Components/NectarExpenses';
import NectarHarvests from './Components/NectarHarvests';
import NectarHiveMovements from './Components/NectarHiveMovements';
import NectarApiaryStrength from './Components/NectarApiaryStrength';
import NectarAdvances from './Components/NectarAdvances';

const SingleApiary = (props) => {
    const { match, history } = props;
    const apiaryId = match?.params?.apiaryId;

    const [apiary, setApiary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [activeTab, setActiveTab] = useState(1);

    const fetchDetails = async () => {
        try {
            const data = await getApiaryDetails(apiaryId);
            const apiaryData = data && Array.isArray(data) ? data[0] : (data?.data ? data.data : data);
            setApiary(apiaryData);
        } catch (error) {
            console.error("Failed to load apiary details", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (apiaryId) {
            fetchDetails();
        }
    }, [apiaryId]);

    const handleToggleActive = async () => {
        const isCurrentlyActive = apiary.is_active !== false;
        const action = isCurrentlyActive ? 'disable' : 'enable';

        const result = await Swal.fire({
            title: isCurrentlyActive ? 'Disable this Apiary?' : 'Enable this Apiary?',
            text: isCurrentlyActive
                ? 'This apiary will be hidden from active lists. You can re-enable it later.'
                : 'This apiary will be visible in active lists again.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: isCurrentlyActive ? '#d63031' : '#00b894',
            confirmButtonText: isCurrentlyActive ? 'Yes, Disable' : 'Yes, Enable',
            cancelButtonText: 'Cancel',
        });

        if (!result.isConfirmed) return;

        setActionLoading(true);
        try {
            if (action === 'disable') {
                await disableApiary(apiaryId);
                setApiary(prev => ({ ...prev, is_active: false }));
                toast.success('Apiary disabled successfully');
            } else {
                await enableApiary(apiaryId);
                setApiary(prev => ({ ...prev, is_active: true }));
                toast.success('Apiary enabled successfully');
            }
        } catch (error) {
            const msg = error.response?.data?.error || `Failed to ${action} apiary`;
            toast.error(msg);
        } finally {
            setActionLoading(false);
        }
    };

    const handleDelete = async () => {
        const firstConfirm = await Swal.fire({
            title: 'Delete this Apiary?',
            html: `<p style="color:#636e72">This action is <b>irreversible</b>. The apiary and all associated weather data will be permanently deleted.</p>
                   <p style="font-size:13px;color:#b2bec3">Apiary: <b>${apiary.apiary_name}</b> (${apiaryId})</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d63031',
            confirmButtonText: 'Delete Permanently',
            cancelButtonText: 'Cancel',
        });

        if (!firstConfirm.isConfirmed) return;

        setActionLoading(true);
        try {
            await deleteApiary(apiaryId);
            toast.success('Apiary deleted permanently');
            history.push('/nector-apiaries');
        } catch (error) {
            const errData = error.response?.data;
            if (error.response?.status === 409 && errData?.linked_records) {
                // Show linked records that block deletion
                const recordsList = errData.linked_records.join(', ');
                Swal.fire({
                    title: 'Cannot Delete',
                    html: `<p>This apiary has linked records that must be removed first:</p>
                           <p style="font-weight:bold;color:#d63031">${recordsList}</p>
                           <p style="font-size:13px;color:#636e72">${errData.suggestion || 'Use Disable instead to soft-delete.'}</p>`,
                    icon: 'error',
                    confirmButtonText: 'OK',
                });
            } else {
                toast.error(errData?.error || 'Failed to delete apiary');
            }
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) return <div className="p-5 text-center">Loading Apiary Details...</div>;
    if (!apiary) return <div className="p-5 text-center">Apiary not found</div>;

    const isActive = apiary.is_active !== false;

    return (
        <div className="page-container single-lead-page">
            <div className="top-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span className="back" onClick={() => history.push('/nector-apiaries')}>
                        <i className="fa fa-2x fa-chevron-left"></i>
                    </span>
                    <h2 className="lead-name" style={{ marginBottom: 0 }}>
                        <span className="name">{apiary.apiary_name}</span>
                        <span style={{ fontSize: '16px', color: '#777', marginLeft: '10px' }}>
                            - {apiaryId}
                        </span>
                    </h2>
                </div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <button
                        onClick={handleToggleActive}
                        disabled={actionLoading}
                        style={{
                            padding: '8px 18px',
                            borderRadius: '8px',
                            border: 'none',
                            fontWeight: '600',
                            fontSize: '14px',
                            cursor: actionLoading ? 'not-allowed' : 'pointer',
                            background: isActive ? '#fff5f5' : '#e6fffa',
                            color: isActive ? '#d63031' : '#00b894',
                            border: `1px solid ${isActive ? '#ffa8a8' : '#b2f2bb'}`,
                            transition: 'all 0.2s',
                            opacity: actionLoading ? 0.6 : 1,
                        }}
                    >
                        <i className={`fa ${isActive ? 'fa-ban' : 'fa-check-circle'}`} style={{ marginRight: '6px' }}></i>
                        {actionLoading ? 'Processing...' : (isActive ? 'Disable' : 'Enable')}
                    </button>
                    <button
                        onClick={handleDelete}
                        disabled={actionLoading}
                        style={{
                            padding: '8px 18px',
                            borderRadius: '8px',
                            border: '1px solid #ffa8a8',
                            fontWeight: '600',
                            fontSize: '14px',
                            cursor: actionLoading ? 'not-allowed' : 'pointer',
                            background: '#d63031',
                            color: '#fff',
                            transition: 'all 0.2s',
                            opacity: actionLoading ? 0.6 : 1,
                        }}
                    >
                        <i className="fa fa-trash" style={{ marginRight: '6px' }}></i>
                        Delete
                    </button>
                </div>
            </div>

            <Tabs
                activeKey={activeTab}
                id="apiary-tabs"
                onSelect={k => setActiveTab(parseInt(k))}
            >
                <Tab
                    eventKey={1}
                    title={
                        <div className="lead-tab-header">
                            <i className="fa fa-info-circle"></i>
                            <span> Overview</span>
                        </div>
                    }
                >
                    <ApiaryProfile apiary={apiary} />
                </Tab>

                <Tab
                    eventKey={2}
                    title={
                        <div className="lead-tab-header">
                            <i className="fa fa-eye"></i>
                            <span> Observations</span>
                        </div>
                    }
                >
                    <NectarObservations apiaryId={apiary?.apiary_id || apiaryId} />
                </Tab>

                <Tab
                    eventKey={3}
                    title={
                        <div className="lead-tab-header">
                            <i className="fas fa-rupee-sign"></i>
                            <span> Apiary Expenses</span>
                        </div>
                    }
                >
                        <NectarExpenses 
                            apiaryId={apiary?.apiary_id || apiaryId} 
                            onNavigateToAdvances={() => setActiveTab(7)}
                        />
                    </Tab>
                    
                    <Tab
                        eventKey={4}
                        title={
                            <div className="lead-tab-header">
                                <i className="fa fa-cubes"></i>
                                <span> Harvests</span>
                            </div>
                        }
                    >
                        <NectarHarvests apiaryId={apiary?.apiary_id || apiaryId} />
                    </Tab>

                    <Tab
                        eventKey={5}
                        title={
                            <div className="d-flex align-items-center">
                                <i className="fa fa-truck mr-2"></i>
                                Activity & Movements
                            </div>
                        }
                    >
                        <NectarHiveMovements apiaryId={apiary?.apiary_id || apiaryId} />
                    </Tab>

                    <Tab
                        eventKey={6}
                        title={
                            <div className="lead-tab-header">
                                <i className="fa fa-heartbeat"></i>
                                <span> Apiary Strength</span>
                            </div>
                        }
                    >
                        <NectarApiaryStrength 
                            apiaryId={apiary?.apiary_id || apiaryId} 
                            refreshApiary={fetchDetails}
                        />
                    </Tab>

                    <Tab
                        eventKey={7}
                        title={
                            <div className="lead-tab-header">
                                <i className="fa fa-money"></i>
                                <span> Advances</span>
                            </div>
                        }
                    >
                        <NectarAdvances 
                            apiaryId={apiary?.apiary_id || apiaryId} 
                            onNavigateToExpenses={() => setActiveTab(3)}
                        />
                    </Tab>

            </Tabs>
        </div>
    );
};

export default SingleApiary;
